import React, { createContext, useContext, useState, useEffect } from 'react';
import { settingsAPI } from '@/lib/api';
import { formatDate as formatDateUtil, formatDateShort as formatDateShortUtil, formatTime as formatTimeUtil, formatDateTime as formatDateTimeUtil } from '@/lib/dateUtils';

const DateFormatContext = createContext();

export function DateFormatProvider({ children }) {
  const [dateFormat, setDateFormat] = useState('DD/MM/YYYY');
  const [timeFormat, setTimeFormat] = useState('24h');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const token = localStorage.getItem('ihostpms_token');
        if (token) {
          const resp = await settingsAPI.get();
          if (resp.data?.date_format) {
            setDateFormat(resp.data.date_format);
          }
          if (resp.data?.time_format) {
            setTimeFormat(resp.data.time_format);
          }
        }
      } catch (err) {
        console.error('Failed to load date/time format settings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  // Update date format (called from Settings page)
  const updateDateFormat = (newFormat) => {
    setDateFormat(newFormat);
  };

  // Update time format (called from Settings page)
  const updateTimeFormat = (newFormat) => {
    setTimeFormat(newFormat);
  };

  // Convenience functions to format dates/times using current settings
  const formatDate = (date) => formatDateUtil(date, dateFormat);
  const formatDateShort = (date) => formatDateShortUtil(date, dateFormat);
  const formatTime = (time) => formatTimeUtil(time, timeFormat);
  const formatDateTime = (dateTime) => formatDateTimeUtil(dateTime, dateFormat, timeFormat);

  return (
    <DateFormatContext.Provider value={{ 
      dateFormat, 
      timeFormat,
      setDateFormat: updateDateFormat,
      setTimeFormat: updateTimeFormat,
      formatDate,
      formatDateShort,
      formatTime,
      formatDateTime,
      loading 
    }}>
      {children}
    </DateFormatContext.Provider>
  );
}

export function useDateFormat() {
  const context = useContext(DateFormatContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      setDateFormat: () => {},
      setTimeFormat: () => {},
      formatDate: (date) => formatDateUtil(date, 'DD/MM/YYYY'),
      formatDateShort: (date) => formatDateShortUtil(date, 'DD/MM/YYYY'),
      formatTime: (time) => formatTimeUtil(time, '24h'),
      formatDateTime: (dateTime) => formatDateTimeUtil(dateTime, 'DD/MM/YYYY', '24h'),
      loading: false
    };
  }
  return context;
}
